import { Component, ElementRef, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { DialogRef, DialogService } from "@ngneat/dialog";
import Swal from "sweetalert2";
import { toggleAnimation } from "src/app/shared/animations";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { MessagePortalComponent } from "src/app/portal/portal";
import { PositioningService } from "src/app/shared/services/positioning.service";
import { DatatableColumn } from "src/app/shared/components/base-table/base-table.component";
import { TaxRateCategory } from "src/app/shared/types/tax-rate-categories";
import { TaxRateCategoriesService } from "src/app/shared/services/tax-rate-categories.service";

@Component({
  selector: "app-configuration-tax-rate-categories",
  templateUrl: "./tax-rates.configuration.component.html",
  animations: [toggleAnimation],
  standalone: false,
})
export class TaxRateCategories implements OnInit {
  loading: boolean = false;
  isEditing: boolean = false;
  public cols: DatatableColumn[] = [];
  public form!: FormGroup;
  public categories: TaxRateCategory[] = [];
  public editCategory: TaxRateCategory = {} as TaxRateCategory;

  @ViewChild("dropdownPlaceholder") dropdownPlaceholder!: ElementRef;
  @ViewChild(MessagePortalComponent) messagePortalComponent!: MessagePortalComponent;
  @ViewChild("addCategoryModalTemplate") addCategoryModalTemplate!: TemplateRef<unknown>;

  addCategoryModal!: DialogRef;
  private dialog = inject(DialogService);

  constructor(
    public translate: TranslateService,
    private taxRateService: TaxRateCategoriesService,
    private positioningService: PositioningService,
  ) {}

  ngOnInit(): void {
    this.getColumns();
    this.fetchTaxRateCategories();
  }

  getColumns(): void {
    this.cols = [
      { field: "title", title: this.translate.instant("tax_categories.title"), sort: false } as DatatableColumn,
      { field: "taxLabel", title: this.translate.instant("tax_categories.category"), sort: false } as DatatableColumn,
      { field: "taxType", title: this.translate.instant("tax_categories.tax_type"), sort: false } as DatatableColumn,
      { field: "code", title: this.translate.instant("tax_categories.code"), sort: false } as DatatableColumn,
      { field: "percentage", title: this.translate.instant("tax_categories.percentage"), sort: false } as DatatableColumn,
      { field: "desicionNumber", title: this.translate.instant("tax_categories.desicionNumber"), sort: false } as DatatableColumn,
      { field: "actions", title: this.translate.instant("actions"), hide: false, sort: false } as DatatableColumn,
    ];
  }

  fetchTaxRateCategories(): void {
    this.loading = true;
    const fetchSubscription = this.taxRateService.getTaxRateCategories().subscribe({
      next: (response) => {
        fetchSubscription.unsubscribe();
        this.loading = false;
        this.categories = [...response];
      },
      error: (err) => {
        fetchSubscription.unsubscribe();
        this.loading = false;
        showMessage(PopupType.Danger, err?.message || "Failed to load tax categories");
      },
    });
  }

  onPortalAttached(): void {
    setTimeout(() => {
      this.positioningService.setPosition(this.dropdownPlaceholder, this.messagePortalComponent);
    }, 0);
  }

  openModalForCreate(): void {
    this.isEditing = false;
    this.editCategory = {} as TaxRateCategory;
    this.addCategoryModal = this.dialog.open(this.addCategoryModalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  openModalForEdit(category: TaxRateCategory): void {
    this.isEditing = true;
    this.editCategory = category;
    this.addCategoryModal = this.dialog.open(this.addCategoryModalTemplate, {
      id: `dialog-${crypto.randomUUID()}`,
    });
  }

  closeModal(): void {
    this.addCategoryModal?.close();
    this.fetchTaxRateCategories();
    this.isEditing = false;
    this.editCategory = {} as TaxRateCategory;
  }

  handleDelete(category: TaxRateCategory): void {
    Swal.fire({
      icon: "warning",
      title: this.translate.instant("tax_categories_data.delete_title"),
      text: `${this.translate.instant("tax_categories_data.delete_info")} ${category.title}?`,
      showCancelButton: true,
      confirmButtonText: this.translate.instant("blogs.yes"),
      cancelButtonText: this.translate.instant("blogs.no"),
      customClass: {
        confirmButton: "swal2-confirm btn btn-primary",
        cancelButton: "swal2-cancel btn btn-secondary",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        const deleteSub = this.taxRateService.deleteTaxRateCategory(category.id).subscribe({
          next: () => {
            deleteSub.unsubscribe();
            this.loading = false;
            this.fetchTaxRateCategories();
            showMessage(PopupType.Success, this.translate.instant("tax_categories_data.deleted_successfully"));
          },
          error: (error) => {
            deleteSub.unsubscribe();
            this.loading = false;
            showMessage(PopupType.Danger, error?.error?.toString() || error?.message || "Error deleting category", error);
          },
        });
      }
    });
  }
}
